// ════════════════════════════════════════════════════════════════════════
//  Der Test: eine Zeile, eine Uhr, sonst nichts
//
//  BEWUSST OHNE Tastaturbild und ohne Fingerfarben. Gemessen wird, was das Kind
//  kann – nicht, wie gut die Hilfe ist. Und die zweite Messung muss unter
//  denselben Bedingungen entstehen wie die erste, sonst vergleicht man zwei
//  verschiedene Aufgaben.
//
//  Aus demselben Grund gibt es hier keine Sterne: der Test bewertet nicht, er
//  misst.
// ════════════════════════════════════════════════════════════════════════

import { useEffect } from "react";
import { wordsPerMinute } from "@otto/lehrgang";
import type { Assessment } from "@otto/lehrgang";
import { ExerciseLine } from "../../practice/components/ExerciseLine.js";
import { useAssessment } from "../hooks/useAssessment.js";
import type { Measurement } from "../hooks/useAssessment.js";
import type { Phase } from "../lib/assessment.js";
import styles from "../../../shared/components/shell.module.css";
import own from "./assessment.module.css";

/** Ganze Wörter: eine Nachkommastelle wäre eine Genauigkeit, die niemand hat. */
export function wpm(measurement: { chars: number; seconds: number }): number {
  return Math.round(wordsPerMinute(measurement));
}

export function AssessmentScreen({
  assessment,
  phase,
  before,
  onDone,
  onCancel,
}: {
  assessment: Assessment;
  phase: Phase;
  /** Der Eingangswert, wenn es ihn gibt – nur für den Vergleich am Ende. */
  before?: { chars: number; seconds: number } | undefined;
  onDone: (measurement: Measurement) => void;
  onCancel: () => void;
}) {
  const run = useAssessment(assessment.text);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === " ") event.preventDefault();
      run.handleKey(event.key);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [run]);

  if (run.done) {
    const jetzt = wpm(run.done);
    const vorher = before ? wpm(before) : null;

    return (
      <div className={`${styles.column} ${own.done}`}>
        <h1 className={styles.title}>Geschafft!</h1>
        <p className={own.number}>
          {jetzt} <span className={own.unit}>Wörter pro Minute</span>
        </p>

        {/* Nur der Vergleich mit dem eigenen Anfang. Nie mit anderen Kindern. */}
        {vorher !== null && (
          <p className={styles.footnote}>
            Am Anfang waren es <strong>{vorher}</strong>. {compliment(vorher, jetzt)}
          </p>
        )}

        <button type="button" className={styles.button} onClick={() => onDone(run.done!)}>
          Weiter
        </button>
      </div>
    );
  }

  return (
    <div className={styles.column}>
      <h1 className={styles.title}>{assessment.title}</h1>
      <p className={styles.subtitle}>{assessment.intro}</p>
      {phase === "nachher" && (
        <p className={styles.footnote}>
          Denselben Text hast du am Anfang schon einmal getippt. Jetzt schauen wir, was
          sich verändert hat.
        </p>
      )}

      <ExerciseLine
        state={run.state}
        missCount={run.missCount}
        shaking={false}
        onShakeEnd={() => {}}
      />

      <p className={styles.footnote}>
        {run.started ? "Die Zeit läuft." : "Die Zeit startet beim ersten Buchstaben."}{" "}
        <button type="button" className={styles.textButton} onClick={onCancel}>
          Abbrechen
        </button>
      </p>
    </div>
  );
}

/**
 * Ein Satz zum Ergebnis – und keiner, der etwas verspricht.
 *
 * Auch der Fall „langsamer als vorher“ braucht einen: ein Kind, das an dem Tag
 * müde war, soll nicht vor einer Zahl ohne Worte stehen.
 */
function compliment(before: number, now: number): string {
  if (now > before) return "Das ist schneller geworden – schau, was Üben macht.";
  if (now === before) return "Gleich schnell wie am Anfang.";
  return "Heute war es langsamer. Das passiert, und morgen sieht es wieder anders aus.";
}
